import { useFormContext } from 'react-hook-form';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@components/ui/select';
import LocationSearchInput from './LocationSearchInput';

interface ContactSectionProps {
  enquiryType: string;
}

const stateOptions = [
  'Victoria',
  'New South Wales',
  'Queensland',
  'South Australia',
  'Western Australia',
  'Tasmania',
  'Northern Territory',
  'Australian Capital Territory',
  'Other'
];

export default function ContactSection({ enquiryType }: ContactSectionProps) {
  const form = useFormContext();
  const isEvent = enquiryType === 'Event Enquiry';
  const nameField = isEvent ? 'fullName' : 'name';
  
  return (
    <section className="pb-12 mb-12 border-b border-border">
      <div className="text-center mb-8">
        <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground block mb-3">
          Step 01
        </span>
        <h2 className="text-[28px] md:text-[32px] font-bold text-foreground uppercase tracking-[0.025em] mb-3">
          Contact Information
        </h2>
        <p className="text-sm text-muted-foreground">
          Please provide your details
        </p>
      </div>
      
      <div className="space-y-6">
        <FormField
          control={form.control}
          name={nameField}
          rules={{ required: 'Name is required' }}
          render={({ field }) => (
            <FormItem data-field={field.name}>
              <FormLabel className="text-sm font-semibold text-foreground">
                Full Name <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input 
                  placeholder="John Smith" 
                  {...field}
                  className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                />
              </FormControl>
              <FormMessage className="text-[13px]" />
            </FormItem>
          )}
        />
        
        <div className="grid md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="email"
            rules={{
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Please enter a valid email address'
              }
            }}
            render={({ field }) => (
              <FormItem data-field={field.name}>
                <FormLabel className="text-sm font-semibold text-foreground">
                  Email <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input 
                    type="email" 
                    placeholder="john@example.com" 
                    {...field}
                    className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                  />
                </FormControl>
                <FormMessage className="text-[13px]" />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="contactNumber"
            rules={{ required: 'Contact number is required' }}
            render={({ field }) => (
              <FormItem data-field={field.name}>
                <FormLabel className="text-sm font-semibold text-foreground">
                  Contact Number <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input 
                    type="tel" 
                    placeholder="0400 000 000" 
                    {...field}
                    className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                  />
                </FormControl>
                <FormMessage className="text-[13px]" />
              </FormItem>
            )}
          />
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="suburb"
            render={({ field }) => (
              <FormItem data-field={field.name}>
                <FormLabel className="text-sm font-semibold text-foreground">
                  Location
                </FormLabel>
                <FormControl>
                  <LocationSearchInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Search for suburb or address"
                  />
                </FormControl>
                <FormMessage className="text-[13px]" />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem data-field={field.name}>
                <FormLabel className="text-sm font-semibold text-foreground">
                  State
                </FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {stateOptions.map((state) => (
                      <SelectItem key={state} value={state} className="text-sm">
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage className="text-[13px]" />
              </FormItem>
            )}
          />
        </div>
      </div>
    </section>
  );
}
